import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Edit, Mail, Phone, MapPin, CreditCard, TrendingUp } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const VendorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [bills, setBills] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVendorDetails();
  }, [id]);

  const fetchVendorDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      // Fetch vendor details, bills, and balance in parallel
      const [vendorRes, billsRes, balanceRes] = await Promise.all([
        axios.get(`${API_URL}/vendors/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/vendors/${id}/bills`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/vendors/${id}/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setVendor(vendorRes.data?.data);
      setBills(billsRes.data?.data || []);
      setBalance(balanceRes.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch vendor details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      unpaid: 'bg-yellow-100 text-yellow-800',
      partially_paid: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !vendor) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || 'Vendor not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/accounting/vendors')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{vendor.vendor_name}</h1>
            <p className="text-sm text-gray-500">{vendor.vendor_number}</p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => navigate(`/accounting/vendors/${id}/edit`)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit Vendor
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Bills</div>
              <div className="text-2xl font-bold text-gray-900">{vendor.bill_count || 0}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Purchases</div>
              <div className="text-xl font-bold text-gray-900">
                {formatCurrency(vendor.total_purchases)}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <CreditCard className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Outstanding Balance</div>
              <div className="text-xl font-bold text-red-600">
                {formatCurrency(vendor.outstanding_balance)}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${vendor.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
              <span
                className={`text-sm font-medium ${vendor.is_active ? 'text-green-800' : 'text-gray-800'}`}
              >
                {vendor.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <div className="text-sm text-gray-500">Status</div>
              <div className="text-lg font-semibold text-gray-900 capitalize">
                {vendor.vendor_type?.replace('_', ' ')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
          <div className="space-y-3">
            {vendor.contact_person && (
              <div>
                <div className="text-sm text-gray-500">Contact Person</div>
                <div className="text-gray-900 font-medium">{vendor.contact_person}</div>
              </div>
            )}
            {vendor.phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Phone</div>
                  <div className="text-gray-900">{vendor.phone}</div>
                </div>
              </div>
            )}
            {vendor.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <div className="text-gray-900">{vendor.email}</div>
                </div>
              </div>
            )}
            {vendor.address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <div className="text-sm text-gray-500">Address</div>
                  <div className="text-gray-900">{vendor.address}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h2>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-500">Payment Terms</div>
              <div className="text-gray-900 font-medium capitalize">
                {vendor.payment_terms?.replace('_', ' ') || '-'}
              </div>
            </div>
            {vendor.preferred_payment_method && (
              <div>
                <div className="text-sm text-gray-500">Preferred Payment Method</div>
                <div className="text-gray-900 capitalize">
                  {vendor.preferred_payment_method.replace('_', ' ')}
                </div>
              </div>
            )}
            {vendor.credit_limit > 0 && (
              <div>
                <div className="text-sm text-gray-500">Credit Limit</div>
                <div className="text-gray-900 font-medium">
                  {formatCurrency(vendor.credit_limit)}
                </div>
              </div>
            )}
            {vendor.tax_id && (
              <div>
                <div className="text-sm text-gray-500">Tax ID</div>
                <div className="text-gray-900">{vendor.tax_id}</div>
              </div>
            )}
          </div>
        </div>

        {/* Bank Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Bank Information</h2>
          <div className="space-y-3">
            {vendor.bank_name && (
              <div>
                <div className="text-sm text-gray-500">Bank Name</div>
                <div className="text-gray-900 font-medium">{vendor.bank_name}</div>
              </div>
            )}
            {vendor.bank_account_name && (
              <div>
                <div className="text-sm text-gray-500">Account Name</div>
                <div className="text-gray-900">{vendor.bank_account_name}</div>
              </div>
            )}
            {vendor.bank_account_number && (
              <div>
                <div className="text-sm text-gray-500">Account Number</div>
                <div className="text-gray-900 font-mono">{vendor.bank_account_number}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bills List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Bills History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Bill #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Bill Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Due Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Balance Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bills.length > 0 ? (
                bills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">
                      {bill.bill_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(bill.bill_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(bill.due_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(bill.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                      <span
                        className={
                          parseFloat(bill.balance_due) > 0 ? 'text-red-600' : 'text-gray-900'
                        }
                      >
                        {formatCurrency(bill.balance_due)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getStatusBadge(bill.status)}`}
                      >
                        {bill.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/accounting/bills/${bill.id}`)}
                        className="text-amber-600 hover:text-amber-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No bills found for this vendor
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      {vendor.notes && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Notes</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{vendor.notes}</p>
        </div>
      )}
    </div>
  );
};

export default VendorDetailPage;
